import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { api, qs } from "@/lib/api";
import { BRANDS } from "@/lib/brands";
import { formatDate, formatInt, mountainDay, mountainToISO } from "@/lib/format";
import { centsToInput, toCents } from "@/lib/money";
import { BrandMark } from "@/components/BrandMark";
import { Empty, PageHeader } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import type { Product, Promotion } from "./catalog";

const DISCOUNTS: [string, string][] = [
  ["percent", "Percent off"],
  ["amount", "Dollars off the order"],
  ["price_override", "Special price per item"],
  ["free_shipping", "Free shipping"],
];

function promoState(p: Promotion): { label: string; variant: "success" | "muted" | "warning" } {
  const now = Date.now();
  if (!p.active) return { label: "off", variant: "muted" };
  if (p.ends_at && new Date(p.ends_at).getTime() < now) return { label: "ended", variant: "muted" };
  if (p.starts_at && new Date(p.starts_at).getTime() > now) return { label: "not started", variant: "warning" };
  if (p.max_redemptions && p.codes.reduce((n, c) => n + c.redemptions, 0) >= p.max_redemptions) return { label: "used up", variant: "muted" };
  return { label: "running", variant: "success" };
}

/** Billing → Promotions: discounts and the codes buyers type at checkout (not case-sensitive). */
export function PromotionsPage() {
  const [brand, setBrand] = useState<number | "">("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [editing, setEditing] = useState<Promotion | "new" | null>(null);
  const q = useQuery({
    queryKey: ["promos", brand, activeOnly],
    queryFn: () => api<{ promos?: Promotion[]; promotions?: Promotion[] }>("/api/v1/promos" + qs({ brand_id: brand || undefined, active_only: activeOnly || undefined, limit: 500 })),
  });
  const rows = q.data?.promos ?? q.data?.promotions ?? [];
  const products = useQuery({ queryKey: ["products", "all-for-promos"], queryFn: () => api<{ products: Product[] }>("/api/v1/products" + qs({ limit: 500 })), staleTime: 60_000 });
  const productName = (id: number) => products.data?.products.find((p) => p.id === id)?.name ?? `#${id}`;
  return (
    <div className="mx-auto max-w-[1200px] space-y-3">
      <PageHeader
        title="Promotions"
        sub="Discount codes buyers type at checkout"
        actions={
          <Button size="sm" onClick={() => setEditing("new")}>
            <Plus /> New promotion
          </Button>
        }
      >
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Codes aren't case-sensitive (AZREIA and azreia are the same code). A promotion can have several codes; each code or the whole promotion can be switched off.
        </p>
      </PageHeader>
      <Card className="overflow-x-auto">
        <div className="flex flex-wrap items-center gap-2 border-b p-3">
          <Select className="h-8 w-auto" value={brand} onChange={(e) => setBrand(e.target.value ? Number(e.target.value) : "")} aria-label="Brand">
            <option value="">All brands</option>
            {BRANDS.map((b) => (
              <option key={b.id} value={b.id}>
                {b.short}
              </option>
            ))}
          </Select>
          <label className="flex items-center gap-1.5 text-sm">
            <Checkbox checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} /> Switched-on only
          </label>
        </div>
        {q.isLoading && <Skeleton className="m-3 h-32" />}
        {q.error && <p className="px-4 py-3 text-sm text-[var(--color-danger-fg)]">{q.error.message}</p>}
        {!q.isLoading && !q.error && rows.length === 0 && <Empty title="No promotions here.">Press New promotion to make one.</Empty>}
        {rows.length > 0 && (
          <Table>
            <THead>
              <tr>
                <TH>Promotion</TH>
                <TH>Discount</TH>
                <TH>Codes (times used)</TH>
                <TH>Applies to</TH>
                <TH>When</TH>
                <TH>Status</TH>
                <TH className="w-px" />
              </tr>
            </THead>
            <tbody>
              {rows.map((p) => {
                const st = promoState(p);
                return (
                  <TR key={p.id} className={st.variant === "muted" ? "row-dim" : ""}>
                    <TD className="max-w-[16rem]">
                      <div className="font-medium">{p.name}</div>
                      <div className="flex items-center gap-1.5 text-xs text-[var(--color-muted-foreground)]">
                        {p.brand_id ? <BrandMark id={p.brand_id} /> : "every brand"}
                        {p.keap_id ? ", from Keap" : ""}
                      </div>
                    </TD>
                    <TD className="text-sm">{p.summary}</TD>
                    <TD className="max-w-[16rem]">
                      <span className="flex flex-wrap gap-1">
                        {p.codes.map((c) => (
                          <Badge key={c.id} variant={c.active ? "default" : "muted"} title={c.active ? undefined : "switched off"}>
                            <span className="num">{c.code}</span> ({formatInt(c.redemptions)})
                          </Badge>
                        ))}
                        {p.codes.length === 0 && <span className="text-xs text-[var(--color-warning-fg)]">no codes</span>}
                      </span>
                    </TD>
                    <TD className="max-w-[14rem] text-xs">
                      {p.applies_to_product_ids?.length ? p.applies_to_product_ids.map(productName).join(", ") : "every product"}
                      {p.min_subtotal_cents ? <div className="text-[var(--color-muted-foreground)]">orders over ${centsToInput(p.min_subtotal_cents)}</div> : null}
                    </TD>
                    <TD className="text-xs whitespace-nowrap">
                      {p.starts_at || p.ends_at ? `${p.starts_at ? formatDate(p.starts_at) : "now"} to ${p.ends_at ? formatDate(p.ends_at) : "no end"}` : "any time"}
                      {(p.max_redemptions || p.per_contact_limit) && (
                        <div className="text-[var(--color-muted-foreground)]">
                          {p.max_redemptions ? `${formatInt(p.max_redemptions)} uses total` : ""}
                          {p.max_redemptions && p.per_contact_limit ? ", " : ""}
                          {p.per_contact_limit ? `${p.per_contact_limit} per person` : ""}
                        </div>
                      )}
                    </TD>
                    <TD>
                      <Badge variant={st.variant}>{st.label}</Badge>
                    </TD>
                    <TD>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(p)}>
                        Edit
                      </Button>
                    </TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>
      {editing && <PromoDialog promo={editing === "new" ? null : editing} products={products.data?.products ?? []} onClose={() => setEditing(null)} />}
    </div>
  );
}

const dayOf = (iso: string | null) => (iso ? mountainDay(new Date(iso)) : "");

/** promos_create / promos_update (add_codes, disable_codes, active). Dates are whole days in Mountain Time. */
function PromoDialog({ promo, products, onClose }: { promo: Promotion | null; products: Product[]; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(promo?.name ?? "");
  const [brand, setBrand] = useState<number | "">(promo?.brand_id ?? "");
  const [type, setType] = useState(promo?.discount_type ?? "percent");
  const [value, setValue] = useState(promo ? (promo.discount_type === "percent" ? String(promo.discount_value / 100) : centsToInput(promo.discount_value)) : "");
  const [productIds, setProductIds] = useState<number[]>(promo?.applies_to_product_ids ?? []);
  const [minSubtotal, setMinSubtotal] = useState(centsToInput(promo?.min_subtotal_cents));
  const [start, setStart] = useState(dayOf(promo?.starts_at ?? null));
  const [end, setEnd] = useState(dayOf(promo?.ends_at ?? null));
  const [maxUses, setMaxUses] = useState(promo?.max_redemptions ? String(promo.max_redemptions) : "");
  const [perPerson, setPerPerson] = useState(promo?.per_contact_limit ? String(promo.per_contact_limit) : "");
  const [active, setActive] = useState(promo?.active ?? true);
  const [newCodes, setNewCodes] = useState<string[]>([]);
  const [typedCode, setTypedCode] = useState("");
  const [disable, setDisable] = useState<string[]>([]);
  const pct = type === "percent" ? Number(value) : NaN;
  const cents = type === "amount" || type === "price_override" ? toCents(value) : null;
  const valueOk = type === "free_shipping" || (type === "percent" ? pct > 0 && pct <= 100 : cents !== null && cents > 0);
  const existing = new Set((promo?.codes ?? []).map((c) => c.code.toLowerCase()));
  const addCode = () => {
    const c = typedCode.trim();
    if (!c) return;
    if (!/^[A-Za-z0-9_-]{2,40}$/.test(c)) return toast("Codes are letters, numbers, dashes or underscores (2 to 40).", "error");
    if (existing.has(c.toLowerCase()) || newCodes.some((x) => x.toLowerCase() === c.toLowerCase())) return toast(`${c} is already on this promotion.`, "error");
    setNewCodes([...newCodes, c]);
    setTypedCode("");
  };
  const save = useMutation({
    mutationFn: () => {
      const dates = { starts_at: start ? mountainToISO(start, "00:00") : undefined, ends_at: end ? mountainToISO(end, "23:59") : undefined };
      const amounts = type === "percent" ? { percent: pct } : type === "free_shipping" ? {} : { amount_cents: cents };
      if (promo) {
        return api(`/api/v1/promos/${promo.id}`, {
          method: "PATCH",
          json: {
            name: name.trim(),
            ...amounts,
            product_ids: productIds,
            min_subtotal_cents: minSubtotal ? toCents(minSubtotal) : 0,
            ...dates,
            max_redemptions: maxUses ? Number(maxUses) : 0,
            per_contact_limit: perPerson ? Number(perPerson) : 0,
            active,
            add_codes: newCodes.length ? newCodes : undefined,
            disable_codes: disable.length ? disable : undefined,
          },
        });
      }
      return api("/api/v1/promos", {
        json: {
          name: name.trim(),
          brand_id: brand || undefined,
          discount_type: type,
          ...amounts,
          product_ids: productIds.length ? productIds : undefined,
          min_subtotal_cents: minSubtotal ? (toCents(minSubtotal) ?? undefined) : undefined,
          ...dates,
          max_redemptions: maxUses ? Number(maxUses) : undefined,
          per_contact_limit: perPerson ? Number(perPerson) : undefined,
          codes: newCodes,
        },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["promos"] });
      toast(promo ? "Promotion saved" : "Promotion created");
      onClose();
    },
    onError: (e) => toast(e.message, "error"),
  });
  const brandProducts = products.filter((p) => !p.archived_at && (!brand || p.brand_id === brand));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] w-[min(640px,calc(100vw-2rem))] overflow-y-auto">
        <DialogTitle>{promo ? `Edit ${promo.name}` : "New promotion"}</DialogTitle>
        <DialogDescription>{promo ? "Codes already used keep their history; switching a code off stops new uses." : "Buyers type one of its codes at checkout. Dates are whole days, Mountain Time."}</DialogDescription>
        <div className="space-y-3 text-sm">
          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <div className="space-y-1">
              <Label htmlFor="pm-name">Name (only staff see it)</Label>
              <Input id="pm-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="AZREIA members" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pm-brand">Brand</Label>
              <Select id="pm-brand" value={brand} disabled={!!promo} onChange={(e) => setBrand(e.target.value ? Number(e.target.value) : "")}>
                <option value="">Every brand</option>
                {BRANDS.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.short}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="pm-type">Discount</Label>
              <Select id="pm-type" value={type} disabled={!!promo} onChange={(e) => setType(e.target.value)}>
                {DISCOUNTS.map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </Select>
            </div>
            {type !== "free_shipping" && (
              <div className="space-y-1">
                <Label htmlFor="pm-value">{type === "percent" ? "Percent off" : type === "amount" ? "Dollars off" : "New price per item ($)"}</Label>
                <Input id="pm-value" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} placeholder={type === "percent" ? "25" : "100"} />
              </div>
            )}
          </div>
          {value && !valueOk && <p className="text-xs text-[var(--color-danger-fg)]">{type === "percent" ? "A percent from 1 to 100." : "An amount like 100 or 49.95."}</p>}
          <fieldset className="space-y-1 rounded-md border p-3">
            <legend className="px-1 text-xs font-semibold">Applies to</legend>
            <p className="text-xs text-[var(--color-muted-foreground)]">Tick none for every product{brand ? " of this brand" : ""}.</p>
            <div className="grid max-h-40 gap-1 overflow-y-auto sm:grid-cols-2">
              {brandProducts.map((p) => (
                <label key={p.id} className="flex items-center gap-1.5">
                  <Checkbox checked={productIds.includes(p.id)} onChange={(e) => setProductIds(e.target.checked ? [...productIds, p.id] : productIds.filter((x) => x !== p.id))} />
                  <span className="truncate">{p.name}</span>
                </label>
              ))}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Label htmlFor="pm-min" className="text-xs">
                Only on orders over ($, optional)
              </Label>
              <Input id="pm-min" className="h-8 w-28" inputMode="decimal" value={minSubtotal} onChange={(e) => setMinSubtotal(e.target.value)} />
            </div>
          </fieldset>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="pm-start">Starts (optional)</Label>
              <Input id="pm-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pm-end">Ends after (optional)</Label>
              <Input id="pm-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pm-max">Uses in total (optional)</Label>
              <Input id="pm-max" type="number" min={0} value={maxUses} onChange={(e) => setMaxUses(e.target.value)} placeholder="no limit" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pm-per">Uses per person (optional)</Label>
              <Input id="pm-per" type="number" min={0} value={perPerson} onChange={(e) => setPerPerson(e.target.value)} placeholder="no limit" />
            </div>
          </div>
          {start && end && end < start && <p className="text-xs text-[var(--color-danger-fg)]">It ends before it starts.</p>}
          <fieldset className="space-y-2 rounded-md border p-3">
            <legend className="px-1 text-xs font-semibold">Codes</legend>
            {(promo?.codes ?? []).length > 0 && (
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {promo!.codes.map((c) => (
                  <label key={c.id} className="flex items-center gap-1.5" title={`${formatInt(c.redemptions)} uses`}>
                    <Checkbox
                      checked={c.active && !disable.includes(c.code)}
                      disabled={!c.active}
                      onChange={(e) => setDisable(e.target.checked ? disable.filter((x) => x !== c.code) : [...disable, c.code])}
                    />
                    <span className="num">{c.code}</span>
                    <span className="text-xs text-[var(--color-muted-foreground)]">
                      {c.active ? `${formatInt(c.redemptions)} used` : "off"}
                    </span>
                  </label>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-1.5">
              {newCodes.map((c) => (
                <Badge key={c} variant="default">
                  <span className="num">{c}</span>
                  <button aria-label={`Remove ${c}`} onClick={() => setNewCodes(newCodes.filter((x) => x !== c))}>
                    <X className="size-3" />
                  </button>
                </Badge>
              ))}
              <form
                className="flex items-center gap-1"
                onSubmit={(e) => {
                  e.preventDefault();
                  addCode();
                }}
              >
                <Input className="h-8 w-44" value={typedCode} onChange={(e) => setTypedCode(e.target.value)} placeholder="Type a code, then Enter" aria-label="New code" />
              </form>
            </div>
            {promo && disable.length > 0 && <p className="text-xs text-[var(--color-warning-fg)]">Switching off: {disable.join(", ")}. A switched-off code can't be switched back on; add a new one instead.</p>}
          </fieldset>
          {promo && (
            <label className="flex items-center gap-2">
              <Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} /> Promotion on (switch off to stop every code at once)
            </label>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={save.isPending || !name.trim() || !valueOk || (!promo && newCodes.length === 0) || !!(start && end && end < start)}
            title={!promo && newCodes.length === 0 ? "Add at least one code" : undefined}
            onClick={() => save.mutate()}
          >
            {promo ? "Save promotion" : "Create promotion"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
